<template>
  <div class="sub-wizard wizard-container">
    <div class="card card-wizard active" data-color="green" id="wizardProfile">
      <form @submit.prevent>
        <div class="card-header text-center"  v-if="tabCount > 1">
          <h4 class="text-left card-title">{{$t('general.step_completion', [activeTabIndex + 1, tabCount])}}</h4>
          <div class="wizard-navigation">
            <div class="progress-with-circle">
              <div
                class="progress-bar"
                role="progressbar"
                aria-valuenow="1"
                aria-valuemin="1"
                aria-valuemax="3"
                :style="{ width: `${progress}%` }"
              ></div>
            </div>
            <ul class="nav nav-pills">
              <li
                v-for="(tab, index) in tabs"
                :key="tab.title"
                role="tab"
                :tabindex="tab.checked ? 0 : ''"
                :id="`step-${tab.tabId}`"
                :aria-controls="tab.tabId"
                :aria-disabled="!tab.active"
                :aria-selected="tab.active"
                :ref="`tab-${index}`"
                class="nav-item wizard-tab-link"
                :style="linkWidth"
              >
              </li>
            </ul>
          </div>
        </div>
        <div class="card-body">
          <div class="tab-content">
            <slot :activeIndex="activeTabIndex" :activeTab="activeTab"></slot>
          </div>
        </div>
        <div class="card-footer">
          <slot name="footer" :next-tab="nextTab" :prev-tab="prevTab">
            <div class="d-flex justify-content-between">
              <div class="text-left">
                <base-button v-if="canSave" type="default" wide @click.native="doSaveProgress">{{$t('general.save_progress')}}</base-button>
              </div>

              <div class="text-right">
                <base-button v-if="canPrev" type="primary" wide @click.native="doPrev">{{$t('general.previous_step')}}</base-button>
                <base-button v-if="canNext" type="primary" wide @click.native="doNext">{{$t('general.next_step')}}</base-button>
                <base-button v-if="canSave && canFinish" type="primary" wide @click.native="doFinish">{{$t('general.submit_application')}}</base-button>
              </div>
            </div>

            <div class="clearfix"></div>
          </slot>
        </div>
      </form>
    </div>
  </div>
</template>
<script>
  import {throttle} from './throttle';
  import {EventBus} from '@/event-bus.js';

  export default {
    name: 'grant-wizard',
    props: {
      startIndex: {
        type: Number,
        default: 0
      },
      title: {
        type: String
      },
      subTitle: {
        type: String
      },
      prevButtonText: {
        type: String,
        default: 'Previous'
      },
      nextButtonText: {
        type: String,
        default: 'Next'
      },
      finishButtonText: {
        type: String,
        default: 'Finish'
      },
      disableFinishButton: {
        type: Boolean,
        default: false
      },
      vertical: {
        type: Boolean
      },
      nickname: {
        type: String,
        default: ''
      }
    },
    components: {
      TabItemContent: {
        functional: true,
        props: ['tab'],
        render(h, {props}) {
          let content = props.tab.$slots.label;
          if (content && content.length) {
            return content;
          }
          return h('span', [props.tab.$slots.label, props.tab.label])
        }
      },
      TabItemTitle: {
        functional: true,
        props: ['tab'],
        render(h, {props}) {
          let content = props.tab.$slots.tabItemTitle;
          if (content && content.length) {
            return content;
          }
          return h('span', [props.tab.$slots.tabItemTitle, props.tab.tabItemTitle])
        }
      }
    },
    provide() {
      return {
        addTab: this.addTab,
        removeTab: this.removeTab,
        nextTab: this.nextTab,
      };
    },
    inject: ['masterInstance'],
    data() {
      return {
        tabs: [],
        activeTabIndex: 0,
        tabLinkWidth: 0,
        tabLinkHeight: 50
      };
    },
    computed: {
      canNext() {
        return (this.activeTabIndex < this.tabCount - 1) || (this.isLastStep && !this.masterInstance.isLastStep);
      },
      canPrev() {
        return (this.activeTabIndex > 0) || (this.isFirstStep && !this.masterInstance.isFirstStep);
      },
      canFinish() {
        return this.isLastStep && this.masterInstance.isLastStep && !this.disableFinishButton;
      },
      canSave() {
        return this.masterInstance.isEditable;
      },
      isLastStep() {
        return this.activeTabIndex === this.tabCount - 1
      },
      isFirstStep() {
        return this.activeTabIndex === 0;
      },

      tabCount() {
        return this.tabs.length;
      },
      linkWidth() {
        let width = 100;
        if (this.tabCount > 0) {
          width = 100 / this.tabCount;
        }
        if (this.vertical) {
          width = 100;
        }
        return {width: `${width}%`};
      },
      activeTab() {
        return this.tabs[this.activeTabIndex];
      },
      stepPercentage() {
        return (1 / (this.tabCount * 1)) * 100;
        // return (1 / (this.tabCount * 2)) * 100;
      },
      progress() {
        let percentage = 0;
        if (this.activeTabIndex > 0) {
          let stepsToAdd = 1;
          // let stepMultiplier = 2;
          let stepMultiplier = 1; // changed made here for sub wizard
          percentage =
            this.stepPercentage *
            (this.activeTabIndex * stepMultiplier + stepsToAdd);
        } else {
          percentage = this.stepPercentage;
        }
        return percentage;
      }
    },
    methods: {
      doPrev() {
        if (this.activeTabIndex > 0) {
          this.prevTab()
        } else if (this.isFirstStep && !this.masterInstance.isFirstStep) {
          this.masterInstance.prevTab();
        }
      },
      doNext() {
        EventBus.$emit('wizard-next-step');

        if (this.activeTabIndex < this.tabCount - 1) {
          this.nextTab();
        } else if (this.isLastStep && !this.masterInstance.isLastStep) {
          this.masterInstance.nextTab();
        }
      },
      async doSaveProgress() {
        EventBus.$emit('wizard-save-progress');
      },
      async doFinish() {
        let isValid = await this.validate();
        if (isValid) {
          EventBus.$emit('wizard-complete-progress');
        }
      },
      addTab(tab) {
        const index = this.$slots.default.indexOf(tab.$vnode);
        let tabTitle = tab.title || '';
        tab.tabId = `${tabTitle.replace(/ /g, '')}${index}`;
        if (!this.activeTab && index === 0) {
          tab.active = true;
          tab.checked = true;
        }
        if (this.activeTab === tab.name) {
          tab.active = true;
          tab.checked = true;
        }
        this.tabs.splice(index, 0, tab);
      },
      removeTab(tab) {
        const tabs = this.tabs;
        const index = tabs.indexOf(tab);
        if (index > -1) {
          tabs.splice(index, 1);
        }
      },
      validate(tab) {
        if (!this.masterInstance.isEditable) {
          return Promise.resolve(true);
        }

        let tabToValidate = tab || this.activeTab;
        let beforeChange = tabToValidate.beforeChange;
        if (beforeChange) {
          return Promise.resolve(beforeChange())
            .then(res => {
              this.activeTab.hasError = res ? false : true;
              return res;
            })
            .catch(() => {
              this.activeTab.hasError = true;
            });
        } else {
          return Promise.resolve(true);
        }
      },
      async nextTab() {
        let isValid = await this.validate();
        if (isValid && this.activeTabIndex < this.tabCount - 1) {
          this.activeTabIndex++;
        }
        return isValid;
      },
      prevTab() {
        this.activeTabIndex--;
      },
      async navigateToTab(index) {
        if (this.tabs[index].checked) {
          // recursively validate each tab
          if (index > this.activeTabIndex) {
            let valid = await this.nextTab();
            if (valid) {
              this.navigateToTab(index);
            }
          } else {
            this.activeTabIndex = index;
          }
        }
      },
      onResize() {
        let tabLinks = document.getElementsByClassName('wizard-tab-link');
        if (tabLinks.length > 0 && this.tabCount > 0) {
          let {clientWidth, clientHeight} = tabLinks[0];
          this.tabLinkWidth = clientWidth;
          this.tabLinkHeight = clientHeight;
        }
      },
      scrollToTop() {
        window.scrollTo(0, 0);
        const container = document.querySelector('.main-panel');
        if (container) {
          container.scrollTop = 0;
        }
      }
    },
    created() {
      this.masterInstance.doHideControls();
    },
    mounted() {
      EventBus.$on('set-active-tab', tab => {
        if (tab.masterNickname === this.nickname) {
          this.masterInstance.activeTabIndex = tab.masterIndex;
          this.activeTabIndex = tab.childIndex;

          if(tab.validate !== undefined && tab.validate) {
            this.validate();  // to mark as red fields
          }
        }
      })

      this.activeTabIndex = this.startIndex;
      this.$nextTick(() => {
        this.tabs[this.activeTabIndex].active = true;
        this.tabs[this.activeTabIndex].checked = true;
        this.onResize();
      });
      window.addEventListener(
        'resize',
        () => {
          throttle(this.onResize, 40);
        },
        false
      );

      setTimeout(function () {
        $('#applicationComponent textarea').each(function () {
          if (this.scrollHeight !== undefined) {
            $(this).css('height', this.scrollHeight)
          }
        })
      }, 200);

    },
    watch: {
      activeTabIndex(newValue, oldValue) {
        this.masterInstance.doHideControls();
        this.scrollToTop();

        setTimeout(function () {
          $('#applicationComponent textarea').each(function () {
            if (this.scrollHeight !== undefined) {
              $(this).css('height', this.scrollHeight)
            }
          })
        }, 200);

        if (newValue !== oldValue) {
          let oldTab = this.tabs[oldValue];
          let newTab = this.tabs[newValue];
          oldTab.active = false;
          newTab.active = true;

          if (!newTab.checked) {
            newTab.checked = true;
          }
          this.$emit('tab-change', oldTab, newTab);
          this.$emit('update:startIndex', newValue);
        }
      }
    }
  };
</script>
<style lang="scss" scoped>
  /* Tab content animation */


  /**
        Extra niceties. Display error tabs and disable navigation unvisited tabs
       */

  .sub-wizard .card-header {
    padding-top: 0;
    margin-top: 0;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .sub-wizard {
    .wizard-navigation {
      margin: 0px auto 25px !important;
    }

    .tab-content {
      padding-top: 0;
    }

    .card-title {
      margin-bottom: 0;
    }

    .card-body {
      padding-top: 0;
      margin-top: 0;
    }
  }

  /*.sub-wizard .wizard-navigation {*/
  /*margin: 0px auto 25px !important;*/
  /*}*/

  .card-wizard .wizard-navigation {
    /*margin: 0px auto 60px !important;*/
  }

  .wizard-navigation .nav-link {
    &.active,
    &.checked {
      cursor: pointer;
    }
  }

  p.wizard-tab-title {
    margin-top: 2rem;
    font-size: 12px !important;
  }

  .disabled-wizard-link {
    cursor: not-allowed;
  }
</style>
